import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useMemo, useEffect, useState } from "react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { fetchAllProducts } from "@/lib/catalog";
import { ProductCard } from "@/components/site/ProductCard";
import { seo } from "@/lib/seo";

const qo = queryOptions({ queryKey: ["all-products"], queryFn: fetchAllProducts });

type ProductSearch = {
  product?: string;
  tea_type?: string;
  format?: string;
  grade?: string;
  origin?: string;
  cultivation?: string;
  color?: string;
  harvest_2025?: string;
  harvest_2026?: string;
};

export const Route = createFileRoute("/collections/")({
  validateSearch: (search: Record<string, unknown>): ProductSearch => {
    return {
      product: search.product as string | undefined,
      tea_type: search.tea_type as string | undefined,
      format: search.format as string | undefined,
      grade: search.grade as string | undefined,
      origin: search.origin as string | undefined,
      cultivation: search.cultivation as string | undefined,
      color: search.color as string | undefined,
      harvest_2025: search.harvest_2025 as string | undefined,
      harvest_2026: search.harvest_2026 as string | undefined,
    };
  },
  head: () => ({
    meta: [
      ...seo({
        title: "Product Catalog – QT Foods Wholesale",
        description: "Explore our full range of wholesale Japanese food products",
      }),
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(qo),
  component: Page,
});

function Page() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: allProducts } = useSuspenseQuery(qo);

  const productFilter = search.product || "";
  const teaTypeFilter = search.tea_type || "";
  const formatFilter = search.format || "";
  const gradeFilter = search.grade || "";
  const originFilter = search.origin || "";
  const cultivationFilter = search.cultivation || "";
  const colorFilter = search.color || "";
  const harvest2025Filter = search.harvest_2025 || "";
  const harvest2026Filter = search.harvest_2026 || "";

  const setProductFilter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, product: val || undefined }) });
  const setTeaTypeFilter = (val: string) =>
    navigate({
      search: (prev) => ({
        ...prev,
        tea_type: val || undefined,
        ...(val && val !== "Matcha" ? { color: undefined } : {}),
      }),
    });
  const setFormatFilter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, format: val || undefined }) });
  const setGradeFilter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, grade: val || undefined }) });
  const setOriginFilter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, origin: val || undefined }) });
  const setCultivationFilter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, cultivation: val || undefined }) });
  const setColorFilter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, color: val || undefined }) });
  const setHarvest2025Filter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, harvest_2025: val || undefined }) });
  const setHarvest2026Filter = (val: string) =>
    navigate({ search: (prev) => ({ ...prev, page: 1, harvest_2026: val || undefined }) });

  const products = useMemo(
    () =>
      (
        Array.from(
          new Set(allProducts.map((p: any) => p.specs?.product as string).filter(Boolean)),
        ) as string[]
      ).sort((a, b) => a.localeCompare(b)),
    [allProducts],
  );
  const teaTypes = useMemo(
    () =>
      (
        Array.from(
          new Set(allProducts.map((p: any) => p.specs?.tea_type as string).filter(Boolean)),
        ) as string[]
      ).sort((a, b) => a.localeCompare(b)),
    [allProducts],
  );
  const formats = useMemo(
    () =>
      (
        Array.from(
          new Set(allProducts.map((p: any) => p.specs?.format as string).filter(Boolean)),
        ) as string[]
      ).sort((a, b) => a.localeCompare(b)),
    [allProducts],
  );
  const grades = useMemo(() => {
    const g = Array.from(
      new Set(allProducts.map((p: any) => p.specs?.grade as string).filter(Boolean)),
    ) as string[];
    const topOrder = ["Ceremonial", "Premium", "Culinary"];
    return g.sort((a, b) => {
      const idxA = topOrder.indexOf(a);
      const idxB = topOrder.indexOf(b);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [allProducts]);
  const origins = useMemo(() => {
    const originSet = new Set<string>();
    allProducts.forEach((p: any) => {
      const o = p.specs?.origin as string;
      if (o) {
        if (o.includes(" and ")) {
          o.split(" and ").forEach((part) => originSet.add(part.trim()));
        } else {
          originSet.add(o.trim());
        }
      }
    });
    return Array.from(originSet)
      .filter((r) => r !== "Hagi" && r !== "Takayama")
      .sort((a, b) => a.localeCompare(b));
  }, [allProducts]);
  const cultivations = useMemo(
    () =>
      (
        Array.from(
          new Set(allProducts.map((p: any) => p.specs?.cultivation as string).filter(Boolean)),
        ) as string[]
      )
        .filter((t) => t !== "Handcrafted" && t !== "Premium")
        .sort((a, b) => a.localeCompare(b)),
    [allProducts],
  );
  const colors = ["Dark Green", "Green", "Vibrant Green", "Deep Green", "Light Green"];

  const filteredProducts = useMemo(() => {
    return allProducts.filter((p: any) => {
      if (productFilter && p.specs?.product !== productFilter) return false;
      if (teaTypeFilter && p.specs?.tea_type !== teaTypeFilter) return false;
      if (formatFilter && p.specs?.format !== formatFilter) return false;
      if (gradeFilter && p.specs?.grade !== gradeFilter) return false;
      if (originFilter) {
        const o = p.specs?.origin as string;
        if (!o || !o.includes(originFilter)) return false;
      }
      if (cultivationFilter && p.specs?.cultivation !== cultivationFilter) return false;
      if (colorFilter && p.specs?.color !== colorFilter) return false;
      if (harvest2025Filter === "Available") {
        const hasPrice = !!p.specs?.["2025 Stock Price"];
        const notes = p.specs?.["Availability Notes"] || "";
        if (!hasPrice && !notes.includes("2025 harvest")) return false;
      }
      if (harvest2026Filter === "Available") {
        const hasPrice = !!p.specs?.["2026 Stock Price"];
        const notes = p.specs?.["Availability Notes"] || "";
        if (!hasPrice && !notes.includes("2026 harvest")) return false;
      }
      return true;
    });
  }, [
    allProducts,
    productFilter,
    teaTypeFilter,
    formatFilter,
    gradeFilter,
    originFilter,
    cultivationFilter,
    colorFilter,
    harvest2025Filter,
    harvest2026Filter,
  ]);

  // Group products by category
  const groupedProducts = useMemo(() => {
    const groups: Record<string, any[]> = {};
    filteredProducts.forEach((p: any) => {
      const cat = p.specs?.product || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });

    const getPrice = (p: any) => {
      const p26 = p.specs?.["2026 Stock Price"] as string | undefined;
      const p25 = p.specs?.["2025 Stock Price"] as string | undefined;

      const parsePrice = (priceStr?: string) => {
        if (!priceStr) return Infinity;
        const match = priceStr.match(/([\d,]+)/);
        if (match) {
          return parseInt(match[1].replace(/,/g, ""), 10);
        }
        return Infinity;
      };

      const val26 = parsePrice(p26);
      if (val26 !== Infinity) return val26;
      return parsePrice(p25);
    };

    // Sort products by price within each category
    for (const cat in groups) {
      groups[cat].sort((a, b) => {
        return getPrice(a) - getPrice(b);
      });
    }

    return groups;
  }, [filteredProducts]);

  const sortedCategories = Object.keys(groupedProducts).sort((a, b) => {
    const topOrder = ["Tea"];
    const idxA = topOrder.indexOf(a);
    const idxB = topOrder.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  // Scroll Spy logic
  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let currentActives: string[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentActives.push(entry.target.id);
          }
        });
        if (currentActives.length > 0) {
          setActiveSection(currentActives[0]);
        }
      },
      { rootMargin: "-20% 0px -80% 0px" },
    );

    sortedCategories.forEach((cat) => {
      const el = document.getElementById(`section-${cat}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sortedCategories, filteredProducts]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <SiteLayout breadcrumbs={[{ label: "Catalog" }]}>
      <section className="mx-auto max-w-7xl px-5 pt-8 pb-16 lg:px-8 lg:pt-10 lg:pb-24">
        <h1 className="mt-3 font-serif text-5xl md:text-6xl">All Products</h1>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Every product is available wholesale and as a blank-label / private-label option. Add what
          you need to your inquiry and we'll respond with pricing and lead times.
        </p>

        {/* Filters */}
        <div className="mt-12 bg-muted p-6 border border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Origin</label>
            <select
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              className="w-full border-border bg-background p-2 text-sm outline-none"
            >
              <option value="">All Origins</option>
              {origins.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cultivation</label>
            <select
              value={cultivationFilter}
              onChange={(e) => setCultivationFilter(e.target.value)}
              className="w-full border-border bg-background p-2 text-sm outline-none"
            >
              <option value="">All Cultivations</option>
              {cultivations.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Product</label>
            <select
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="w-full border-border bg-background p-2 text-sm outline-none"
            >
              <option value="">All Products</option>
              {products.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tea Variety</label>
            <select
              value={teaTypeFilter}
              onChange={(e) => setTeaTypeFilter(e.target.value)}
              className="w-full border-border bg-background p-2 text-sm outline-none"
            >
              <option value="">All Tea Varieties</option>
              {teaTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          {(!teaTypeFilter || teaTypeFilter === "Matcha") && (
            <div>
              <label className="block text-sm font-medium mb-1">Color</label>
              <select
                value={colorFilter}
                onChange={(e) => setColorFilter(e.target.value)}
                className="w-full border-border bg-background p-2 text-sm outline-none"
              >
                <option value="">All Colors</option>
                {colors.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">2025 Harvest</label>
            <select
              value={harvest2025Filter}
              onChange={(e) => setHarvest2025Filter(e.target.value)}
              className="w-full border-border bg-background p-2 text-sm outline-none"
            >
              <option value="">Show All</option>
              <option value="Available">Available in 2025</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">2026 Harvest</label>
            <select
              value={harvest2026Filter}
              onChange={(e) => setHarvest2026Filter(e.target.value)}
              className="w-full border-border bg-background p-2 text-sm outline-none"
            >
              <option value="">Show All</option>
              <option value="Available">Available in 2026</option>
            </select>
          </div>
        </div>

        <div className="mt-12 flex flex-col lg:flex-row gap-12 items-start relative">
          {/* Main Content (Products) */}
          <div className="flex-1 w-full order-2 lg:order-1">
            <div className="mb-8 text-sm text-muted-foreground flex items-center justify-between">
              <span>
                Showing {filteredProducts.length} product{filteredProducts.length !== 1 && "s"}
              </span>
              {(teaTypeFilter ||
                productFilter ||
                originFilter ||
                cultivationFilter ||
                colorFilter ||
                harvest2025Filter ||
                harvest2026Filter) && (
                <button
                  onClick={() => navigate({ search: {} })}
                  className="text-primary hover:underline"
                >
                  Clear Filters
                </button>
              )}
            </div>

            {filteredProducts.length === 0 ? (
              <p className="text-muted-foreground py-10">
                No products match your selected filters.
              </p>
            ) : (
              <div className="flex flex-col gap-16">
                {sortedCategories.map((cat) => (
                  <div key={cat} id={`section-${cat}`} className="scroll-mt-24">
                    <h2 className="font-serif text-3xl mb-6 pb-2 border-b border-border">{cat}</h2>
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
                      {groupedProducts[cat].map((p: any) => (
                        <ProductCard
                          key={p.id}
                          product={p}
                          categoryHandle={p.categories?.handle || "unknown"}
                          categoryName={p.categories?.name || "Unknown Category"}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sticky Sidebar (Minimap/Table of Contents) */}
          {sortedCategories.length > 0 && (
            <aside className="hidden lg:block w-64 shrink-0 sticky top-24 order-1 lg:order-2 self-start border-l border-border pl-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 text-foreground/80">
                On This Page
              </h3>
              <ul className="flex flex-col gap-2.5">
                {sortedCategories.map((cat) => (
                  <li key={cat}>
                    <button
                      onClick={() => scrollToSection(`section-${cat}`)}
                      className={`text-sm text-left w-full transition-colors ${
                        activeSection === `section-${cat}`
                          ? "text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {cat}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
